import { transition } from '@helpers/animate';
import borderRadius from '@helpers/borderRadius';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { baseGrid } from '@helpers/grid';
import { between, Device, from, until } from '@helpers/media';
import { VariantTheme } from '@helpers/themeEnums';
import { contentStyles, display1 } from '@helpers/typography';
import SVGCheckWhite from '@img/icons/check-in-circle-white.svg?url';
import SVGTrustPilotLogoDesktopOnly from '@img/logos/trustpilot-logo-banner-desktop-only.svg';
import SVGTrustPilotLogo from '@img/logos/trustpilot-logo-banner.svg';
import Button, { LinkButton } from '@stories/Components/Buttons/Button/Button';
import { SubmissionStyles } from '@stories/Components/Misc/Forms/UmbracoForm/Submission/Submission.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const BannerSection = styled.section ``;
const Container = styled(SectionWrapper) `
  --bannerBgColor: ${brand.tealAccessibleDark.base};
  --bannerAccentColor: ${brand.orange._400};

  &[data-variant-theme=${VariantTheme.Black}] {
    --bannerBgColor: ${brand.global.black};
    --bannerAccentColor: ${brand.orange.base};
  }

  &[data-variant-theme=${VariantTheme.Teal}] {
    @media ${from(Device.Tablet)} {
      &::before {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 58%;
        height: 100%;
        background: linear-gradient(
          180deg,
          ${rgba(brand.global.black, 0)} 0%,
          ${rgba(brand.global.black, 0)} 58%,
          ${rgba(brand.global.black, 0.48)} 100%
        );
      }
    }

    @media ${from(Device.ActualDesktop)} {
      &::before {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 57%;
        height: 100%;
        background: linear-gradient(
          180deg,
          ${rgba(brand.global.black, 0)} 0%,
          ${rgba(brand.global.black, 0)} 57%,
          ${rgba(brand.global.black, 0.48)} 100%
        );
      }
    }

    @media ${from(Device.DesktopXL)} {
      &::before {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 55%;
        height: 100%;
        background: linear-gradient(
          180deg,
          ${rgba(brand.global.black, 0)} 0%,
          ${rgba(brand.global.black, 0)} 55%,
          ${rgba(brand.global.black, 0.48)} 100%
        );
      }
    }
  }

  position: relative;
  padding-top: var(--headerHeightWithTopBar);
  background-color: var(--bannerBgColor);
  overflow: hidden;

  @media ${from(Device.Tablet)} {
    background: linear-gradient(
      90deg,
      var(--bannerBgColor) 0%,
      var(--bannerBgColor) 58%,
      ${brand.global.white} 58%,
      ${brand.global.white} 100%
    );
  }

  @media ${from(Device.TabletLarge)} {
    padding-top: var(--headerHeight);
  }

  @media ${from(Device.ActualDesktop)} {
    background: linear-gradient(
      90deg,
      var(--bannerBgColor) 0%,
      var(--bannerBgColor) 57%,
      ${brand.global.white} 57%,
      ${brand.global.white} 100%
    );
  }

  @media ${from(Device.DesktopXL)} {
    background: linear-gradient(
      90deg,
      var(--bannerBgColor) 0%,
      var(--bannerBgColor) 55%,
      ${brand.global.white} 55%,
      ${brand.global.white} 100%
    );
  }

  @media ${until(Device.Tablet)} {
    &[data-has-banner-image='false'] {
      ${SectionWrapperStyles.Inner} {
        padding-bottom: 0;
      }
    }
  }
`;
const BottomBorder = styled.div `
  width: 100%;
  height: ${fluid(12, 16)};
  background-color: ${brand.orange.base};
  border-top: ${fluid(6, 8)} solid ${brand.red.base};
  box-sizing: content-box;
`;
const Inner = styled.div `
  ${baseGrid};
  position: relative;

  @media ${from(Device.Tablet)} {
    align-items: center;
  }

  @media ${until(Device.Tablet)} {
    grid-template-columns: auto;
  }
`;
const Content = styled.div `
  color: ${brand.global.white};

  @media ${from(Device.Tablet)} {
    grid-column: span 7;

    ${Container}[data-has-banner-image='true'] & {
      grid-column: span 5;
    }

    ${Container}[data-has-banner-image='true'][data-has-form='true'] & {
      grid-column: span 5;
    }
  }
`;
const Heading = styled.h1 `
  ${display1};
  max-width: 700px;
`;
const ContentBottom = styled.div `
  display: flex;
  align-items: end;
  justify-content: space-between;
  column-gap: ${fluid(16, 32)};

  ${Container}[data-has-cta='true'] & {
    margin-top: 1em;

    &[data-illustration-default-mobile='true'] {
      margin-top: -3.5em;
    }

    @media ${from(Device.TabletSmall)} {
      margin-top: 1em;

      &[data-illustration-default='true'] {
        margin-top: -2.5em;
      }
    }

    @media ${from(Device.DesktopSmall)} {
      &[data-illustration-default='true'] {
        margin-top: -4.5em;
      }
    }
  }

  ${Container}[data-has-banner-image='true'] &,
  ${Container}[data-has-banner-image='false'][data-has-form='false'] &,
  ${Container}[data-has-cta='false'] & {
    margin-top: ${fluid(32, 64)};
  }
`;
const IntroText = styled.div `
  ${contentStyles};
  max-width: 600px;

  ul {
    list-style: none;
    padding: 0;

    li {
      position: relative;
      padding-left: 32px;
      margin-left: 0;

      &::before {
        position: absolute;
        top: 3px;
        left: 0;
        width: 20px;
        height: 20px;
        margin-left: 0;
        background-image: url(${SVGCheckWhite});
        background-size: 20px;
        background-position: center;
        background-repeat: no-repeat;
      }
    }
  }

  ul:not([style*='list-style-type']) li::before {
    content: '';
  }

  a {
    color: var(--bannerAccentColor);
    text-decoration: underline;
  }

  + ${ContentBottom} {
    ${Container}[data-has-banner-image='false'] & {
      margin-top: 6px;

      @media ${from(Device.TabletSmall)} {
        margin-top: 32px;
      }

      @media ${from(Device.DesktopSmall)} {
        margin-top: 32px;
      }
    }
  }
`;
const Cta = styled(LinkButton) `
  margin-top: 20px;
`;
const FormToggle = styled(Button) `
  margin-top: 20px;
  display: none;

  @media ${from(Device.Tablet)} {
    display: inline-flex;
  }
`;
const FormToggleMobile = styled(Button) `
  margin-top: 20px;
  display: inline-flex;

  @media ${from(Device.Tablet)} {
    display: none;
  }
`;
const TrustPilotLink = styled.a `

  &:focus {
    outline-offset: 3px;
    outline: 2px solid ${brand.global.white};
  }
`;
const TrustPilotLogo = styled(SVGTrustPilotLogo) `
  max-width: 220px;
  height: auto;

  ${Container}[data-has-banner-image='true'] & {
    @media ${from(Device.Tablet)} {
      display: none;
    }
  }
`;
const TrustPilotLogoDesktopOnly = styled(SVGTrustPilotLogoDesktopOnly) `
  display: none;

  @media ${from(Device.Tablet)} {
    display: block;
  }
`;
const BannerIllustrationWrapper = styled.div `
  @media ${until(Device.TabletSmall)} {
    flex-shrink: 0;
  }
`;
const BannerIllustration = styled.img `
  object-fit: cover;

  @media ${from(Device.TabletSmall)} {
    min-width: 290px;
  }

  @media ${from(Device.TabletLarge)} {
    min-width: 340px;
  }

  @media ${until(Device.TabletSmall)} {
    display: none;
  }

  &[data-mobile] {
    display: none;

    @media ${until(Device.TabletSmall)} {
      position: relative;
      display: block;
      width: 148px;
      height: 220px;

      [data-illustration-overwritten-mobile='false'] & {
        transform: translateX(48px);
      }
    }
  }
`;
const Additional = styled.div `
  display: flex;
  justify-content: end;

  &[data-has-image='true'] {
    @media ${from(Device.Tablet)} {
      grid-column: 6 / span 7;
      padding-left: ${fluid(30, 60)};
    }
  }

  @media ${from(Device.Tablet)} {
    grid-column: 8 / span 5;
    padding-left: ${fluid(30, 60)};

    ${Container}[data-has-banner-image='true'][data-has-form='true'] & {
      grid-column: 6 / span 7;
      padding-left: 0;
      min-height: 464px;
    }
  }

  @media ${until(Device.Tablet)} {
    ${Container}[data-has-banner-image='true'] & {
      order: -1;
      margin-bottom: 16px;
    }
  }
`;
const FormArea = styled.div `
  width: 100%;
  color: ${brand.global.black};

  @media ${from(Device.Tablet)} {
    max-width: 544px;
  }

  .umbraco-forms-page {
    margin: 0;

    @media ${until(Device.Tablet)} {
      padding-top: 40px;
      padding-bottom: 40px;
      margin: var(--sectionSpacing) calc(var(--sitePadding) * -1) 0;
      border-radius: 0;
    }
  }

  @media ${between(Device.Tablet, Device.TabletLarge)} {
    &:not([data-form-reveal]) {
      .umbraco-forms-page {
        .row-fluid .col-md-6 {
          grid-column: span 12;
        }
      }
    }
  }

  ${SubmissionStyles.ThankYouMessage} {
    padding: 8px;
  }

  ${SubmissionStyles.StepItem} {
    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }
`;
const Form = styled.div ``;
const BannerImageWrapper = styled.div `
  position: relative;
  display: flex;
  justify-content: end;
  border-radius: ${fluid(borderRadius.xs, borderRadius.sm)};
  overflow: hidden;

  ${FormArea} {
    ${transition('transform')};
    position: absolute;
    top: 0;
    left: 0;
    transform: translateX(-100%) translateX(-1px);

    &[data-form-reveal='true'] {
      transform: translateX(0);
    }

    @media ${until(Device.Tablet)} {
      display: none;
    }
  }
`;
const BannerImage = styled.img `
  border-radius: ${fluid(borderRadius.xs, borderRadius.sm)};

  ${Container}[data-has-banner-image='true'][data-has-form='true'] & {
    object-fit: cover;
  }
`;
export const BannerStyles = {
    Additional,
    BannerIllustration,
    BannerIllustrationWrapper,
    BannerImage,
    BannerImageWrapper,
    BannerSection,
    BottomBorder,
    Container,
    Content,
    ContentBottom,
    Cta,
    Form,
    FormArea,
    FormToggle,
    FormToggleMobile,
    Heading,
    Inner,
    IntroText,
    TrustPilotLink,
    TrustPilotLogo,
    TrustPilotLogoDesktopOnly,
};
